import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Grid,
    Avatar,
    Chip,
    Stack,
    Alert,
    CircularProgress,
    Divider,
    FormHelperText
} from '@mui/material';
import { adminBadgeAPI } from '../../../api/adminBadges';

const BadgeFormDialog = ({
    open,
    onClose,
    badge = null,
    onSave
}) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        iconUrl: '🏆',
        category: 'ACHIEVEMENT',
        rarity: 'COMMON',
        pointsRequired: 0,
        requiredClassifications: 0,
        requiredStreakDays: 0,
        requiredAccuracy: 0,
        requiredFeedbacks: 0
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [categories, setCategories] = useState([]);
    const [rarities, setRarities] = useState([]);
    const [availableIcons, setAvailableIcons] = useState([]);
    const [selectedIconPreview, setSelectedIconPreview] = useState('🏆');

    const isEdit = Boolean(badge);

    useEffect(() => {
        if (open) {
            fetchFormData();
            if (badge) {
                setFormData({
                    name: badge.name || '',
                    description: badge.description || '',
                    iconUrl: badge.iconUrl || '🏆',
                    category: badge.category || 'ACHIEVEMENT',
                    rarity: badge.rarity || 'COMMON',
                    pointsRequired: badge.pointsRequired || 0,
                    requiredClassifications: badge.requiredClassifications || 0,
                    requiredStreakDays: badge.requiredStreakDays || 0,
                    requiredAccuracy: badge.requiredAccuracy || 0,
                    requiredFeedbacks: badge.requiredFeedbacks || 0
                });
                setSelectedIconPreview(badge.iconUrl || '🏆');
            } else {
                setFormData({
                    name: '',
                    description: '',
                    iconUrl: '🏆',
                    category: 'ACHIEVEMENT',
                    rarity: 'COMMON',
                    pointsRequired: 0,
                    requiredClassifications: 0,
                    requiredStreakDays: 0,
                    requiredAccuracy: 0,
                    requiredFeedbacks: 0
                });
                setSelectedIconPreview('🏆');
            }
        }
    }, [open, badge]);

    const fetchFormData = async () => {
        try {
            const [categoriesRes, raritiesRes, iconsRes] = await Promise.all([
                adminBadgeAPI.getCategories(),
                adminBadgeAPI.getRarities(),
                adminBadgeAPI.getAvailableIcons()
            ]);

            setCategories(categoriesRes.data || []);
            setRarities(raritiesRes.data || []);
            setAvailableIcons(iconsRes.data || []);
        } catch (error) {
            console.error('Failed to fetch form data:', error);
            setError('Failed to load form data');
        }
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (field === 'iconUrl') {
            setSelectedIconPreview(value);
        }
    };

    const handleNumberChange = (field) => (event) => {
        const value = Math.max(0, parseInt(event.target.value) || 0);
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            setError('Badge name is required');
            return false;
        }
        if (!formData.description.trim()) {
            setError('Badge description is required');
            return false;
        }
        if (formData.name.length > 50) {
            setError('Badge name must be less than 50 characters');
            return false;
        }
        if (formData.description.length > 200) {
            setError('Badge description must be less than 200 characters');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        setError('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            let response;
            if (isEdit) {
                response = await adminBadgeAPI.updateBadge(badge.id, formData);
            } else {
                response = await adminBadgeAPI.createBadge(formData);
            }

            onSave(response.data);
            handleClose();
        } catch (error) {
            console.error('Failed to save badge:', error);
            setError(error.response?.data?.message || 'Failed to save badge');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            name: '',
            description: '',
            iconUrl: '🏆',
            category: 'ACHIEVEMENT',
            rarity: 'COMMON',
            pointsRequired: 0,
            requiredClassifications: 0,
            requiredStreakDays: 0,
            requiredAccuracy: 0,
            requiredFeedbacks: 0
        });
        setSelectedIconPreview('🏆');
        setError('');
        setLoading(false);
        onClose();
    };

    const getRequirementHelperText = (category) => {
        switch (category) {
            case 'STREAK':
                return 'Set "Required Streak Days" for streak-based badges';
            case 'ACCURACY':
                return 'Set both "Required Classifications" and "Required Accuracy" for accuracy-based badges';
            case 'SPECIAL':
                return 'Set "Required Feedbacks" for feedback-based badges';
            case 'ACHIEVEMENT':
            case 'MILESTONE':
                return 'Set "Required Classifications" for classification-based badges';
            default:
                return 'Set appropriate requirements based on badge category';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 3,
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 2
            }}>
                <Avatar sx={{ background: 'rgba(255,255,255,0.2)' }}>
                    {selectedIconPreview}
                </Avatar>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        {isEdit ? 'Edit Badge' : 'Create New Badge'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {isEdit ? `Editing: ${badge?.name}` : 'Add a new eco achievement badge'}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid item xs={12}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#1b5e20' }}>
                            📝 Basic Information
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Badge Name"
                            value={formData.name}
                            onChange={handleInputChange('name')}
                            required
                            error={!formData.name.trim() && formData.name !== ''}
                            helperText={`${formData.name.length}/50 characters`}
                            inputProps={{ maxLength: 50 }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Badge Icon</InputLabel>
                            <Select
                                value={formData.iconUrl}
                                onChange={handleInputChange('iconUrl')}
                                label="Badge Icon"
                                renderValue={(value) => (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <span style={{ fontSize: '1.5rem' }}>{value}</span>
                                        <span>{value}</span>
                                    </Box>
                                )}
                            >
                                {availableIcons.map((icon) => (
                                    <MenuItem key={icon} value={icon}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span style={{ fontSize: '1.5rem' }}>{icon}</span>
                                            <span>{icon}</span>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={handleInputChange('description')}
                            multiline
                            rows={3}
                            required
                            error={!formData.description.trim() && formData.description !== ''}
                            helperText={`${formData.description.length}/200 characters`}
                            inputProps={{ maxLength: 200 }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={formData.category}
                                onChange={handleInputChange('category')}
                                label="Category"
                            >
                                {categories.map((category) => (
                                    <MenuItem key={category} value={category}>
                                        <Chip
                                            label={category}
                                            size="small"
                                            sx={{
                                                background: 'rgba(67, 160, 71, 0.1)',
                                                color: '#43a047'
                                            }}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{getRequirementHelperText(formData.category)}</FormHelperText>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Rarity</InputLabel>
                            <Select
                                value={formData.rarity}
                                onChange={handleInputChange('rarity')}
                                label="Rarity"
                            >
                                {rarities.map((rarity) => (
                                    <MenuItem key={rarity} value={rarity}>
                                        <Chip
                                            label={rarity}
                                            size="small"
                                            sx={{
                                                background: rarity === 'LEGENDARY' ? '#e91e63' :
                                                          rarity === 'EPIC' ? '#ff9800' :
                                                          rarity === 'RARE' ? '#2196f3' : '#4caf50',
                                                color: 'white'
                                            }}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#1b5e20' }}>
                            🎯 Requirements
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Required Classifications"
                            type="number"
                            value={formData.requiredClassifications}
                            onChange={handleNumberChange('requiredClassifications')}
                            inputProps={{ min: 0 }}
                            helperText="Number of classifications needed"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Points Required"
                            type="number"
                            value={formData.pointsRequired}
                            onChange={handleNumberChange('pointsRequired')}
                            inputProps={{ min: 0 }}
                            helperText="Points needed to unlock (optional)"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Required Streak Days"
                            type="number"
                            value={formData.requiredStreakDays}
                            onChange={handleNumberChange('requiredStreakDays')}
                            inputProps={{ min: 0 }}
                            helperText="Consecutive days needed (for STREAK badges)"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Required Accuracy (%)"
                            type="number"
                            value={formData.requiredAccuracy}
                            onChange={handleNumberChange('requiredAccuracy')}
                            inputProps={{ min: 0, max: 100 }}
                            helperText="Accuracy percentage needed (for ACCURACY badges)"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Required Feedbacks"
                            type="number"
                            value={formData.requiredFeedbacks}
                            onChange={handleNumberChange('requiredFeedbacks')}
                            inputProps={{ min: 0 }}
                            helperText="Accepted corrections needed (for SPECIAL badges)"
                        />
                    </Grid>
                </Grid>

                {/* Preview */}
                <Box sx={{ mt: 4, p: 3, background: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                        🔍 Preview
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ fontSize: '2rem', width: 60, height: 60, background: 'transparent' }}>
                            {selectedIconPreview}
                        </Avatar>
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                {formData.name || 'Badge Name'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {formData.description || 'Badge description will appear here...'}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Chip label={formData.category} size="small" color="success" />
                                <Chip
                                    label={formData.rarity}
                                    size="small"
                                    sx={{
                                        background: formData.rarity === 'LEGENDARY' ? '#e91e63' :
                                                  formData.rarity === 'EPIC' ? '#ff9800' :
                                                  formData.rarity === 'RARE' ? '#2196f3' : '#4caf50',
                                        color: 'white'
                                    }}
                                />
                            </Stack>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 2 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                    sx={{
                        background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #2e7d32, #43a047)'
                        }
                    }}
                >
                    {loading ? 'Saving...' : (isEdit ? 'Update Badge' : 'Create Badge')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BadgeFormDialog;
